import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  AlertCircle,
  CheckCircle,
  DollarSign,
  Ruler,
  Palette,
  MapPin,
  Info,
  Image as ImageIcon,
  Tag,
  Printer,
  BookOpen,
  X,
  Trash2,
  ExternalLink,
  Upload,
  RefreshCw,
  Crop,
  Check,
  Clock,
  Zap,
  FileImage
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';
import LoadingSpinner from '../ui/LoadingSpinner';
import '../../styles/SubmissionSystem.css';
import './CuratorArtworkEdit.css';

const numberOrNull = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  const trimmed = `${value}`.trim();
  if (trimmed === '') {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatArtistName = (user) => {
  if (!user) {
    return 'Unknown Artist';
  }
  return (
    user.nickname ||
    [user.first_name, user.last_name].filter(Boolean).join(' ') ||
    (user.handle ? `@${user.handle}` : '') ||
    user.email ||
    'Unknown Artist'
  );
};

const CuratorArtworkEdit = () => {
  const { vcId } = useParams();
  const navigate = useNavigate();

  // Tab navigation
  const [activeTab, setActiveTab] = useState('general');
  const tabs = [
    { id: 'general', label: 'General' },
    { id: 'images', label: 'Images' },
    { id: 'tags', label: 'Tags' },
    { id: 'story', label: 'Story' },
    { id: 'prints', label: 'Prints' }
  ];

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [artwork, setArtwork] = useState(null);
  const [coverImage, setCoverImage] = useState('');
  const [artistUser, setArtistUser] = useState(null);
  const [accessibleGalleries, setAccessibleGalleries] = useState([]);
  const [mediumOptions, setMediumOptions] = useState([]);
  const [mediumMapping, setMediumMapping] = useState({ attributeId: null });
  const [initialMediumTitle, setInitialMediumTitle] = useState('');
  
  // Images state
  const [mediaElements, setMediaElements] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  
  // Tags state
  const [allTags, setAllTags] = useState([]);
  const [artworkTags, setArtworkTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [addingTag, setAddingTag] = useState(false);
  
  // Prints state
  const [prints, setPrints] = useState([]);
  const [loadingPrints, setLoadingPrints] = useState(false);
  
  // High-res print status state
  const [printStatus, setPrintStatus] = useState(null);
  const [loadingPrintStatus, setLoadingPrintStatus] = useState(false);
  const [uploadingHighRes, setUploadingHighRes] = useState(false);
  const [scanningQuality, setScanningQuality] = useState(false);
  const [analyzingColors, setAnalyzingColors] = useState(false);
  const highResInputRef = useRef(null);
  
  // Print size requirements at 240 DPI (min requirement for quality prints)
  const PRINT_DIMENSIONS_240DPI = {
    '8x10': { width: 1920, height: 2400, label: '8×10"' },
    '11x14': { width: 2640, height: 3360, label: '11×14"' },
    '12x12': { width: 2880, height: 2880, label: '12×12"' },
    '16x20': { width: 3840, height: 4800, label: '16×20"' },
    '18x18': { width: 4320, height: 4320, label: '18×18"' },
    '20x20': { width: 4800, height: 4800, label: '20×20"' },
    '20x24': { width: 4800, height: 5760, label: '20×24"' }
  };
  
  // Image upload state
  const [uploadingImageType, setUploadingImageType] = useState(null);
  const [uploadingCoverImage, setUploadingCoverImage] = useState(false);
  const fileInputRef = useRef(null);
  const coverImageInputRef = useRef(null);
  
  // Story state
  const [storyForm, setStoryForm] = useState({
    inspiration: '',
    process: ''
  });
  const [savingStory, setSavingStory] = useState(false);
  const [storySuccess, setStorySuccess] = useState('');
  
  const [form, setForm] = useState({
    title: '',
    price: '',
    width: '',
    height: '',
    depth: '',
    description: '',
    medium: '',
    access_control: 'Public',
    status: 'For Sale',
    location: '',
    completion_year: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const numericFields = useMemo(() => ['price', 'width', 'height', 'depth'], []);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!form.title.trim()) {
      newErrors.title = 'Title is required.';
    }

    numericFields.forEach((field) => {
      if (form[field] === '') {
        return;
      }
      const parsed = Number(form[field]);
      if (!Number.isFinite(parsed)) {
        newErrors[field] = 'Enter a valid number.';
      } else if (parsed < 0) {
        newErrors[field] = 'Value cannot be negative.';
      }
    });

    if (form.completion_year) {
      if (!/^\d{4}$/.test(form.completion_year)) {
        newErrors.completion_year = 'Enter a 4-digit year.';
      }
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [form, numericFields]);

  const loadMediumOptions = useCallback(async () => {
    try {
      const { data, error: mediumsError } = await supabase
        .from('artwork_attributes')
        .select('id, title')
        .eq('type', 'medium')
        .order('title');

      if (mediumsError) {
        console.error('Error loading medium options:', mediumsError);
        return [];
      }

      setMediumOptions(data || []);
      return data || [];
    } catch (mediumsError) {
      console.error('Error loading medium options:', mediumsError);
      return [];
    }
  }, []);

  const loadAccessibleGalleries = useCallback(async (userUuid, currentLocation) => {
    if (!userUuid && !currentLocation) {
      setAccessibleGalleries([]);
      return;
    }

    try {
      const galleries = [];

      if (userUuid) {
        const { data, error: accessError } = await supabase
          .from('gallery_access')
          .select(`
            gallery_id,
            galleries (
              gallery_id,
              name,
              address
            )
          `)
          .eq('user_uuid', userUuid);

        if (accessError) {
          console.error('Error loading gallery access:', accessError);
        } else if (data) {
          data.forEach((item) => {
            const gallery = item.galleries || {};
            const galleryId = item.gallery_id;
            if (!galleryId) {
              return;
            }
            galleries.push({
              gallery_id: galleryId,
              name: gallery.name || `Gallery ${galleryId}`,
              address: gallery.address || ''
            });
          });
        }
      }

      if (currentLocation && !galleries.some((g) => Number(g.gallery_id) === Number(currentLocation))) {
        const { data: currentGallery, error: currentGalleryError } = await supabase
          .from('galleries')
          .select('gallery_id, name, address')
          .eq('gallery_id', currentLocation)
          .single();

        if (currentGalleryError) {
          console.warn('Could not load current gallery details:', currentGalleryError);
        } else if (currentGallery) {
          galleries.push({
            gallery_id: currentGallery.gallery_id,
            name: currentGallery.name || `Gallery ${currentGallery.gallery_id}`,
            address: currentGallery.address || ''
          });
        }
      }

      galleries.sort((a, b) => a.name.localeCompare(b.name));
      setAccessibleGalleries(galleries);
    } catch (accessError) {
      console.error('Error loading galleries:', accessError);
    }
  }, []);

  const fetchArtworkDetails = useCallback(async () => {
    const { data, error: artworkError } = await supabase
      .from('vcs')
      .select(`
        vc_id,
        title,
        description,
        price,
        width,
        height,
        depth,
        access_control,
        status,
        location,
        artwork_completed_date,
        cover_image,
        more,
        users:users!vcs_uuid_fkey (
          uuid,
          first_name,
          last_name,
          nickname,
          handle,
          email
        )
      `)
      .eq('vc_id', vcId)
      .single();

    if (artworkError) {
      throw artworkError;
    }

    const { data: mediumMappingData, error: mediumError } = await supabase
      .from('attribute_vc_mapping')
      .select(`
        mapping_id,
        attribute_id,
        artwork_attributes!inner(
          id,
          title,
          type
        )
      `)
      .eq('vc_id', vcId)
      .eq('artwork_attributes.type', 'medium')
      .limit(1);

    if (mediumError) {
      console.warn('Could not load current medium:', mediumError);
    }

    return {
      artwork: data,
      mediumMapping: mediumMappingData && mediumMappingData.length > 0 ? mediumMappingData[0] : null
    };
  }, [vcId]);

  // Load media elements for images tab
  const loadMediaElements = useCallback(async () => {
    setLoadingImages(true);
    try {
      const { data, error: mediaError } = await supabase
        .from('media_elements')
        .select('*')
        .eq('vc_id', vcId)
        .order('order', { ascending: true });

      if (mediaError) {
        console.error('Error loading media elements:', mediaError);
      } else {
        setMediaElements(data || []);
      }
    } catch (err) {
      console.error('Error loading media elements:', err);
    } finally {
      setLoadingImages(false);
    }
  }, [vcId]);

  // Load all tags and artwork's current tags
  const loadTags = useCallback(async () => {
    setLoadingTags(true);
    try {
      // Get all available tags (excluding medium type)
      const { data: allTagsData, error: tagsError } = await supabase
        .from('artwork_attributes')
        .select('id, title, type')
        .neq('type', 'medium')
        .eq('approved', true)
        .order('title');

      if (tagsError) {
        console.error('Error loading tags:', tagsError);
      } else {
        setAllTags(allTagsData || []);
      }

      // Get tags currently assigned to this artwork
      const { data: mappingsData, error: mappingsError } = await supabase
        .from('attribute_vc_mapping')
        .select(`
          mapping_id,
          attribute_id,
          artwork_attributes!inner(id, title, type)
        `)
        .eq('vc_id', vcId);

      if (mappingsError) {
        console.error('Error loading artwork tags:', mappingsError);
      } else {
        // Filter out medium type tags
        const tags = (mappingsData || [])
          .filter((m) => m.artwork_attributes?.type !== 'medium')
          .map((m) => ({
            mappingId: m.mapping_id,
            attributeId: m.attribute_id,
            title: m.artwork_attributes?.title || '',
            type: m.artwork_attributes?.type || ''
          }));
        setArtworkTags(tags);
      }
    } catch (err) {
      console.error('Error loading tags:', err);
    } finally {
      setLoadingTags(false);
    }
  }, [vcId]);

  // Load prints/products for this artwork
  const loadPrints = useCallback(async () => {
    setLoadingPrints(true);
    try {
      const { data, error: printsError } = await supabase
        .from('products')
        .select(`
          product_id,
          title,
          description,
          price,
          status,
          quantity,
          size,
          cover_image,
          payment_link,
          created_at
        `)
        .eq('vc_id', vcId)
        .order('created_at', { ascending: false });

      if (printsError) {
        console.error('Error loading prints:', printsError);
      } else {
        setPrints(data || []);
      }
    } catch (err) {
      console.error('Error loading prints:', err);
    } finally {
      setLoadingPrints(false);
    }
  }, [vcId]);

  // Load print status for high-res/quality info
  const loadPrintStatus = useCallback(async () => {
    setLoadingPrintStatus(true);
    try {
      const { data, error: statusError } = await supabase
        .from('artwork_print_status')
        .select('*')
        .eq('vc_id', vcId)
        .is('optin_id', null)
        .maybeSingle();

      if (statusError) {
        console.error('Error loading print status:', statusError);
      } else {
        setPrintStatus(data);
      }
    } catch (err) {
      console.error('Error loading print status:', err);
    } finally {
      setLoadingPrintStatus(false);
    }
  }, [vcId]);

  // Get image dimensions from a File object
  const getImageDimensionsFromFile = (file) => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => {
        reject(new Error('Failed to load image'));
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  // Calculate quality level and available sizes based on dimensions
  const calculateQualityInfo = (width, height) => {
    const maxDim = Math.max(width, height);
    
    // Quality levels based on 240 DPI for 20" prints (4800px needed)
    let qualityLevel = 'low';
    if (maxDim >= 4800) qualityLevel = 'excellent';
    else if (maxDim >= 3600) qualityLevel = 'good';
    else if (maxDim >= 2640) qualityLevel = 'acceptable';

    const availableSizes = [];
    Object.entries(PRINT_DIMENSIONS_240DPI).forEach(([size, dims]) => {
      const fitsLandscape = width >= dims.width && height >= dims.height;
      const fitsPortrait = width >= dims.height && height >= dims.width;
      if (fitsLandscape || fitsPortrait) {
        availableSizes.push(size);
      }
    });

    return { qualityLevel, availableSizes };
  };

  // Handle high-res image upload (admin - up to 100MB)
  const handleHighResUpload = async (file) => {
    if (!file) return;

    setUploadingHighRes(true);
    setError('');

    try {
      // Check file size (100MB limit for admin)
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > 100) {
        throw new Error(`File too large (${fileSizeMB.toFixed(1)}MB). Maximum is 100MB.`);
      }

      // Validate file type - including TIF/TIFF
      const validTypes = ['image/jpeg', 'image/png', 'image/tiff', 'image/heic', 'image/heif', 'image/webp'];
      if (!validTypes.includes(file.type) && !file.name.toLowerCase().match(/\.(jpe?g|png|tiff?|heic|heif|webp)$/)) {
        throw new Error('Invalid file type. Supported: JPEG, PNG, TIFF, HEIC, HEIF, WebP');
      }

      // Generate unique filename
      const timestamp = Date.now();
      const extension = file.name.split('.').pop().toLowerCase();
      const fileName = `highres_${vcId}_${timestamp}.${extension}`;

      // Upload to Supabase Storage bucket 'highres_prints'
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('highres_prints')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true,
          contentType: file.type
        });

      if (uploadError) {
        throw new Error(uploadError.message);
      }

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('highres_prints')
        .getPublicUrl(fileName);

      console.log('Uploaded high-res image to Supabase Storage:', publicUrl);

      // Use Cloudinary to analyze the Supabase image (more accurate, works with TIFF)
      let imageDimensions = { width: 0, height: 0 };
      let imageBytes = file.size;
      
      try {
        // Wait a moment for Supabase to make the file available
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Use Cloudinary fetch to analyze the Supabase URL
        const cloudName = 'vibrantcast';
        const analysisUrl = `https://res.cloudinary.com/${cloudName}/image/fetch/fl_getinfo/${encodeURIComponent(publicUrl)}`;
        
        const response = await fetch(analysisUrl);
        if (response.ok) {
          const data = await response.json();
          const info = data.input || data;
          imageDimensions.width = info.width;
          imageDimensions.height = info.height;
          imageBytes = info.bytes || file.size;
          console.log('Cloudinary analysis:', imageDimensions);
        } else {
          // Fallback to browser-based dimension detection
          console.log('Cloudinary analysis failed, using browser fallback');
          imageDimensions = await getImageDimensionsFromFile(file);
        }
      } catch (analysisErr) {
        console.log('Cloudinary analysis error, using browser fallback:', analysisErr);
        // Fallback to browser-based dimension detection (may not work for TIFF)
        try {
          imageDimensions = await getImageDimensionsFromFile(file);
        } catch {
          // If we can't get dimensions, use placeholder values
          imageDimensions = { width: 0, height: 0 };
        }
      }

      // Calculate quality level and available sizes
      const { qualityLevel, availableSizes } = calculateQualityInfo(imageDimensions.width, imageDimensions.height);

      // Check if record exists
      const { data: existing } = await supabase
        .from('artwork_print_status')
        .select('id')
        .eq('vc_id', vcId)
        .is('optin_id', null)
        .maybeSingle();

      const record = {
        vc_id: Number(vcId),
        optin_id: null,
        highres_image_url: publicUrl,
        image_width: imageDimensions.width,
        image_height: imageDimensions.height,
        image_bytes: imageBytes,
        quality_level: qualityLevel,
        available_sizes: availableSizes,
        quality_checked_at: new Date().toISOString(),
        print_enabled: qualityLevel !== 'low'
      };

      if (existing) {
        await supabase
          .from('artwork_print_status')
          .update(record)
          .eq('id', existing.id);
      } else {
        await supabase
          .from('artwork_print_status')
          .insert(record);
      }

      // Reload print status
      await loadPrintStatus();

      const dimDisplay = imageDimensions.width > 0 
        ? `${imageDimensions.width} × ${imageDimensions.height}px (${qualityLevel})`
        : 'Uploaded - dimensions pending analysis';
      setSuccess(`High-res image uploaded! ${dimDisplay}`);
    } catch (err) {
      console.error('Error uploading high-res image:', err);
      setError('Failed to upload image: ' + err.message);
    } finally {
      setUploadingHighRes(false);
    }
  };

  // Scan existing image for quality - uses Cloudinary to analyze both Cloudinary and Supabase URLs
  const scanExistingImageQuality = async () => {
    // Prefer high-res Supabase image if available, otherwise use cover image
    const imageToScan = printStatus?.highres_image_url || coverImage;
    
    if (!imageToScan) {
      setError('No image to scan. Please add a cover image first.');
      return;
    }

    setScanningQuality(true);
    setError('');

    try {
      // Use Cloudinary to analyze the image (works with both Cloudinary and Supabase URLs)
      const infoUrl = buildCloudinaryAnalysisUrl(imageToScan);
      console.log('Scanning image via Cloudinary:', infoUrl);

      const response = await fetch(infoUrl);
      if (!response.ok) throw new Error('Failed to get image info from Cloudinary');

      const data = await response.json();
      const { width, height, bytes } = data.input || data;

      // Calculate quality info
      const { qualityLevel, availableSizes } = calculateQualityInfo(width, height);

      // Save or update print status
      const { data: existing } = await supabase
        .from('artwork_print_status')
        .select('id, highres_image_url')
        .eq('vc_id', vcId)
        .is('optin_id', null)
        .maybeSingle();

      const record = {
        vc_id: Number(vcId),
        optin_id: null,
        image_width: width,
        image_height: height,
        image_bytes: bytes,
        quality_level: qualityLevel,
        available_sizes: availableSizes,
        quality_checked_at: new Date().toISOString(),
        print_enabled: qualityLevel !== 'low'
      };

      // Preserve existing highres_image_url if we're just rescanning
      if (existing?.highres_image_url) {
        record.highres_image_url = existing.highres_image_url;
      }

      if (existing) {
        await supabase
          .from('artwork_print_status')
          .update(record)
          .eq('id', existing.id);
      } else {
        await supabase
          .from('artwork_print_status')
          .insert(record);
      }

      await loadPrintStatus();
      const sourceLabel = printStatus?.highres_image_url ? 'High-res' : 'Cover image';
      setSuccess(`${sourceLabel} scanned: ${width} × ${height}px (${qualityLevel})`);
    } catch (err) {
      console.error('Error scanning image:', err);
      setError('Failed to scan image: ' + err.message);
    } finally {
      setScanningQuality(false);
    }
  };

  // Build Cloudinary analysis URL - works with both Cloudinary and external URLs (like Supabase)
  const buildCloudinaryAnalysisUrl = (imageUrl) => {
    const cloudName = 'vibrantcast'; // Your Cloudinary cloud name
    
    if (imageUrl.includes('cloudinary.com') && imageUrl.includes('/image/upload/')) {
      // Already a Cloudinary URL - add fl_getinfo
      let analysisUrl = imageUrl.replace('/image/upload/', '/image/upload/fl_getinfo/');
      if (!analysisUrl.includes('.json')) {
        analysisUrl = analysisUrl.replace(/\.[^/.]+$/, '.json');
      }
      return analysisUrl;
    } else {
      // External URL (like Supabase) - use Cloudinary's fetch to analyze it
      // Format: https://res.cloudinary.com/{cloud_name}/image/fetch/fl_getinfo/{external_url}
      const encodedUrl = encodeURIComponent(imageUrl);
      return `https://res.cloudinary.com/${cloudName}/image/fetch/fl_getinfo/${encodedUrl}`;
    }
  };

  // Analyze colors using Cloudinary's color analysis (works with any public URL)
  const analyzeImageColors = async () => {
    const imageUrl = printStatus?.highres_image_url || coverImage;
    if (!imageUrl) {
      setError('No image available for color analysis.');
      return;
    }

    setAnalyzingColors(true);
    setError('');

    try {
      // Build Cloudinary URL - works with both Cloudinary and external URLs
      const analysisUrl = buildCloudinaryAnalysisUrl(imageUrl);
      console.log('Analyzing colors via:', analysisUrl);

      const response = await fetch(analysisUrl);
      if (!response.ok) throw new Error('Failed to get image analysis');

      const data = await response.json();
      
      // Extract color information if available
      const colors = data.colors || data.predominant?.google || [];
      
      if (colors.length > 0) {
        // Update vcs.color_palette with the color data
        const colorPalette = {};
        colors.slice(0, 6).forEach((colorInfo, index) => {
          const colorName = colorInfo[0] || `color_${index}`;
          const percentage = colorInfo[1] || 0;
          if (!colorPalette[colorName]) {
            colorPalette[colorName] = [];
          }
          colorPalette[colorName].push({
            hex: colorInfo[0],
            percentage: parseFloat(percentage)
          });
        });

        await supabase
          .from('vcs')
          .update({ color_palette: colorPalette })
          .eq('vc_id', vcId);

        setSuccess(`Color analysis complete! Found ${colors.length} dominant colors.`);
      } else {
        setSuccess('Color analysis complete. No dominant colors detected.');
      }
    } catch (err) {
      console.error('Error analyzing colors:', err);
      setError('Color analysis failed: ' + err.message);
    } finally {
      setAnalyzingColors(false);
    }
  };

  // Get quality badge color
  const getQualityColor = (level) => {
    switch (level) {
      case 'excellent': return '#16a34a';
      case 'good': return '#2563eb';
      case 'acceptable': return '#ca8a04';
      case 'low': return '#dc2626';
      default: return '#6b7280';
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Add tag to artwork
  const handleAddTag = async (tag) => {
    setAddingTag(true);
    try {
      const { error: addError } = await supabase
        .from('attribute_vc_mapping')
        .insert({ vc_id: Number(vcId), attribute_id: tag.id });

      if (addError) {
        console.error('Error adding tag:', addError);
        setError('Failed to add tag.');
      } else {
        await loadTags();
        setSuccess('Tag added successfully.');
      }
    } catch (err) {
      console.error('Error adding tag:', err);
    } finally {
      setAddingTag(false);
    }
  };

  // Remove tag from artwork
  const handleRemoveTag = async (mappingId) => {
    try {
      const { error: removeError } = await supabase
        .from('attribute_vc_mapping')
        .delete()
        .eq('mapping_id', mappingId);

      if (removeError) {
        console.error('Error removing tag:', removeError);
        setError('Failed to remove tag.');
      } else {
        await loadTags();
        setSuccess('Tag removed.');
      }
    } catch (err) {
      console.error('Error removing tag:', err);
    }
  };

  // Handle image upload for additional images
  const handleImageUpload = async (imageType) => {
    if (fileInputRef.current) {
      fileInputRef.current.dataset.imageType = imageType;
      fileInputRef.current.click();
    }
  };

  const processImageUpload = async (event) => {
    const file = event.target.files?.[0];
    const imageType = event.target.dataset.imageType;
    
    if (!file || !imageType) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be less than 10MB.');
      return;
    }
    
    setUploadingImageType(imageType);
    setError('');
    
    try {
      // Upload to Cloudinary
      const uploadResult = await uploadToCloudinary(
        file,
        'image',
        `artworks/${vcId}`,
        `${vcId}_${imageType.toLowerCase().replace(/[^a-z0-9]/g, '_')}_${Date.now()}`
      );
      
      // Check if a media element already exists for this category title
      const existingMedia = mediaElements.find(
        (m) => m.title?.toLowerCase() === imageType.toLowerCase() && m.type === 'Image'
      );
      
      if (existingMedia) {
        // Update existing media element
        const { error: updateError } = await supabase
          .from('media_elements')
          .update({
            source: uploadResult.url,
            updated_at: new Date().toISOString()
          })
          .eq('element_id', existingMedia.element_id);
        
        if (updateError) throw updateError;
      } else {
        // Insert new media element (matching Flutter schema)
        const { error: insertError } = await supabase
          .from('media_elements')
          .insert({
            vc_id: Number(vcId),
            type: 'Image',
            title: imageType,
            source: uploadResult.url,
            status: 'Public',
            created_at: new Date().toISOString()
          });
        
        if (insertError) throw insertError;
      }
      
      // Refresh media elements
      await loadMediaElements();
      setSuccess(`${imageType} image uploaded successfully.`);
      
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(`Failed to upload image: ${err.message}`);
    } finally {
      setUploadingImageType(null);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Delete additional image
  const handleDeleteImage = async (elementId, imageType) => {
    if (!window.confirm(`Are you sure you want to delete the ${imageType} image?`)) {
      return;
    }
    
    try {
      const { error: deleteError } = await supabase
        .from('media_elements')
        .delete()
        .eq('element_id', elementId);
      
      if (deleteError) throw deleteError;
      
      await loadMediaElements();
      setSuccess(`${imageType} image deleted.`);
    } catch (err) {
      console.error('Error deleting image:', err);
      setError(`Failed to delete image: ${err.message}`);
    }
  };

  // Handle cover image upload
  const processCoverImageUpload = async (event) => {
    const file = event.target.files?.[0];
    
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be less than 10MB.');
      return;
    }
    
    setUploadingCoverImage(true);
    setError('');
    
    try {
      // Upload to Cloudinary
      const uploadResult = await uploadToCloudinary(
        file,
        'image',
        `artworks/${vcId}`,
        `${vcId}_cover_${Date.now()}`
      );
      
      // Update the vcs table with new cover image
      const { error: updateError } = await supabase
        .from('vcs')
        .update({
          cover_image: uploadResult.url,
          updated_at: new Date().toISOString()
        })
        .eq('vc_id', Number(vcId));
      
      if (updateError) throw updateError;
      
      // Update local state
      setCoverImage(uploadResult.url);
      setSuccess('Cover image updated successfully.');
      
    } catch (err) {
      console.error('Error uploading cover image:', err);
      setError(`Failed to upload cover image: ${err.message}`);
    } finally {
      setUploadingCoverImage(false);
      // Reset file input
      if (coverImageInputRef.current) {
        coverImageInputRef.current.value = '';
      }
    }
  };

  // Story save
  const handleSaveStory = async () => {
    setSavingStory(true);
    setStorySuccess('');
    try {
      const { error: storyError } = await supabase
        .from('vcs')
        .update({
          more: {
            inspiration: storyForm.inspiration,
            process: storyForm.process
          },
          updated_at: new Date().toISOString()
        })
        .eq('vc_id', vcId);

      if (storyError) throw storyError;
      setStorySuccess('Story saved successfully.');
      setTimeout(() => setStorySuccess(''), 3000);
    } catch (err) {
      console.error('Error saving story:', err);
      setError('Could not save story. Please try again.');
    } finally {
      setSavingStory(false);
    }
  };

  // Handle tab change and load data
  useEffect(() => {
    if (activeTab === 'images' && mediaElements.length === 0 && !loadingImages) {
      loadMediaElements();
    }
    if (activeTab === 'tags' && allTags.length === 0 && !loadingTags) {
      loadTags();
    }
    if (activeTab === 'prints') {
      if (prints.length === 0 && !loadingPrints) {
        loadPrints();
      }
      if (!printStatus && !loadingPrintStatus) {
        loadPrintStatus();
      }
    }
  }, [activeTab, loadMediaElements, loadTags, loadPrints, loadPrintStatus, mediaElements.length, allTags.length, prints.length, printStatus, loadingImages, loadingTags, loadingPrints, loadingPrintStatus]);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        setLoading(true);
        setError('');

        const [authResult, , artworkResult] = await Promise.all([
          supabase.auth.getUser(),
          loadMediumOptions(),
          fetchArtworkDetails()
        ]);

        const { data: authData } = authResult;
        const { artwork: artworkData, mediumMapping: mediumData } = artworkResult;

        if (!isMounted) {
          return;
        }

        setArtwork(artworkData);
        setArtistUser(artworkData.users || null);
        setCoverImage(artworkData.cover_image || '');

        // Initialize story form from 'more' JSON column
        if (artworkData.more) {
          setStoryForm({
            inspiration: artworkData.more.inspiration || '',
            process: artworkData.more.process || ''
          });
        }

        setForm({
          title: artworkData.title || '',
          price: artworkData.price !== null && artworkData.price !== undefined ? `${artworkData.price}` : '',
          width: artworkData.width !== null && artworkData.width !== undefined ? `${artworkData.width}` : '',
          height: artworkData.height !== null && artworkData.height !== undefined ? `${artworkData.height}` : '',
          depth: artworkData.depth !== null && artworkData.depth !== undefined ? `${artworkData.depth}` : '',
          description: artworkData.description ? artworkData.description.trim() : '',
          medium: mediumData?.artwork_attributes?.title || '',
          access_control: artworkData.access_control || 'Public',
          status: artworkData.status || 'For Sale',
          location: artworkData.location ? `${artworkData.location}` : '',
          completion_year: artworkData.artwork_completed_date
            ? `${new Date(artworkData.artwork_completed_date).getFullYear()}`
            : ''
        });

        if (mediumData?.attribute_id) {
          setMediumMapping({ attributeId: mediumData.attribute_id });
          setInitialMediumTitle(mediumData.artwork_attributes?.title || '');
        } else {
          setMediumMapping({ attributeId: null });
          setInitialMediumTitle('');
        }

        const currentUser = authData?.user || null;
        await loadAccessibleGalleries(currentUser?.id, artworkData.location);
      } catch (initError) {
        console.error('Error loading artwork edit data:', initError);
        if (isMounted) {
          setError('We could not load this artwork. Please try again or contact support.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      isMounted = false;
    };
  }, [fetchArtworkDetails, loadAccessibleGalleries, loadMediumOptions]);

  const handleSave = async () => {
    setError('');
    setSuccess('');

    if (!validateForm()) {
      setError('Please fix the highlighted fields and try again.');
      return;
    }

    const updates = {
      title: form.title.trim(),
      description: form.description ? form.description.trim() : null,
      price: numberOrNull(form.price),
      width: numberOrNull(form.width),
      height: numberOrNull(form.height),
      depth: numberOrNull(form.depth),
      access_control: form.access_control,
      status: form.status,
      location: form.location ? Number(form.location) : null,
      updated_at: new Date().toISOString()
    };

    if (form.completion_year) {
      updates.artwork_completed_date = `${form.completion_year}-01-01`;
    } else {
      updates.artwork_completed_date = null;
    }

    setSaving(true);

    try {
      const { error: updateError } = await supabase
        .from('vcs')
        .update(updates)
        .eq('vc_id', vcId);

      if (updateError) {
        throw updateError;
      }

      const trimmedMedium = form.medium.trim();
      const previousMedium = initialMediumTitle.trim();

      if (trimmedMedium || previousMedium) {
        await handleMediumUpdate(trimmedMedium);
      }

      setArtwork((prev) => (prev ? { ...prev, ...updates } : prev));
      setInitialMediumTitle(trimmedMedium);
      setSuccess('Artwork updated successfully.');
    } catch (saveError) {
      console.error('Error saving artwork:', saveError);
      setError('We could not save your changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleMediumUpdate = async (newMediumTitle) => {
    const trimmedMedium = newMediumTitle.trim();

    if (!trimmedMedium && mediumMapping.attributeId) {
      const { error: deleteError } = await supabase
        .from('attribute_vc_mapping')
        .delete()
        .eq('vc_id', vcId)
        .eq('attribute_id', mediumMapping.attributeId);

      if (deleteError) {
        throw deleteError;
      }

      setMediumMapping({ attributeId: null });
      return;
    }

    if (!trimmedMedium) {
      return;
    }

    let attributeId = mediumOptions.find((option) => option.title.toLowerCase() === trimmedMedium.toLowerCase())?.id;

    if (!attributeId) {
      const { data: insertedAttribute, error: insertAttributeError } = await supabase
        .from('artwork_attributes')
        .insert({ title: trimmedMedium, type: 'medium', approved: true })
        .select('id, title')
        .single();

      if (insertAttributeError) {
        throw insertAttributeError;
      }

      attributeId = insertedAttribute.id;
      setMediumOptions((prev) => {
        const updated = [...prev, insertedAttribute];
        updated.sort((a, b) => a.title.localeCompare(b.title));
        return updated;
      });
    }

    if (mediumMapping.attributeId && mediumMapping.attributeId !== attributeId) {
      const { error: deleteError } = await supabase
        .from('attribute_vc_mapping')
        .delete()
        .eq('vc_id', vcId)
        .eq('attribute_id', mediumMapping.attributeId);

      if (deleteError) {
        throw deleteError;
      }
    }

    const { data: existingMapping, error: mappingLookupError } = await supabase
      .from('attribute_vc_mapping')
      .select('mapping_id')
      .eq('vc_id', vcId)
      .eq('attribute_id', attributeId)
      .maybeSingle();

    if (mappingLookupError) {
      throw mappingLookupError;
    }

    if (!existingMapping) {
      const { error: insertMappingError } = await supabase
        .from('attribute_vc_mapping')
        .insert({ vc_id: Number(vcId), attribute_id: attributeId });

      if (insertMappingError) {
        throw insertMappingError;
      }
    }

    setMediumMapping({ attributeId });
  };

  const renderStatusBadge = () => {
    if (!artwork) {
      return null;
    }
    return (
      <span className="curator-edit-status">
        {artwork.status || 'For Sale'} · {artwork.access_control || 'Public'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="curator-artwork-edit loading-state">
        <LoadingSpinner />
        <p>Loading artwork details...</p>
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="curator-artwork-edit error-state">
        <div className="empty-card">
          <AlertCircle className="icon" />
          <h2>Artwork Not Found</h2>
          <p>We were unable to locate this artwork. It may have been removed.</p>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(-1)}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="curator-artwork-edit">
      <header className="curator-edit-header">
        <button
          type="button"
          className="header-back-btn"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <div className="header-meta">
          <h1>Edit Artwork</h1>
          <p>
            {form.title || 'Untitled'} · {formatArtistName(artistUser)}
          </p>
          {renderStatusBadge()}
        </div>

        <button
          type="button"
          className="header-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <div className="inline-spinner" />
              Saving...
            </>
          ) : (
            <>
              <Save size={16} />
              Save Changes
            </>
          )}
        </button>
      </header>

      {/* Tab Navigation */}
      <nav className="curator-edit-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.id === 'images' && <ImageIcon size={16} />}
            {tab.id === 'tags' && <Tag size={16} />}
            {tab.id === 'story' && <BookOpen size={16} />}
            {tab.id === 'prints' && <Printer size={16} />}
            {tab.label}
          </button>
        ))}
      </nav>

      {(error || success) && (
        <div className="feedback-area">
          {error && (
            <div className="feedback error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="feedback success">
              <CheckCircle size={16} />
              <span>{success}</span>
            </div>
          )}
        </div>
      )}

      {/* General Tab */}
      {activeTab === 'general' && (
        <main className="curator-edit-grid">
          <section className="edit-card image-card">
            <div className="image-wrapper">
              <img
                src={coverImage || '/images/placeholder-artwork.jpg'}
                alt={form.title || 'Artwork'}
                onError={(event) => {
                  event.currentTarget.src = '/images/placeholder-artwork.jpg';
                }}
              />
            </div>
            <p className="image-note">
              Image updates are handled in the Images tab.
            </p>
          </section>

          <section className="edit-card">
            <h2>Basic Details</h2>
            <div className="field-group">
              <label htmlFor="title">Title *</label>
              <input
                id="title"
                type="text"
                value={form.title}
                onChange={(event) => handleFieldChange('title', event.target.value)}
                className={validationErrors.title ? 'has-error' : ''}
                placeholder="Artwork title"
              />
              {validationErrors.title && <p className="field-error">{validationErrors.title}</p>}
            </div>

            <div className="field-row">
              <div className="field-group">
                <label htmlFor="price">
                  <span className="label-icon">
                    <DollarSign size={14} /> Price
                  </span>
                </label>
                <input
                  id="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={(event) => handleFieldChange('price', event.target.value)}
                  className={validationErrors.price ? 'has-error' : ''}
                  placeholder="e.g. 1200"
                />
                {validationErrors.price && <p className="field-error">{validationErrors.price}</p>}
              </div>

              <div className="field-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  value={form.status}
                  onChange={(event) => handleFieldChange('status', event.target.value)}
                >
                  <option value="For Sale">For Sale</option>
                  <option value="Sold">Sold</option>
                  <option value="Not For Sale">Not For Sale</option>
                </select>
              </div>

              <div className="field-group">
                <label htmlFor="access_control">Visibility</label>
                <select
                  id="access_control"
                  value={form.access_control}
                  onChange={(event) => handleFieldChange('access_control', event.target.value)}
                >
                  <option value="Public">Public</option>
                  <option value="Limited">Limited</option>
                  <option value="Archived">Archived</option>
                </select>
              </div>
            </div>
          </section>

          <section className="edit-card">
            <h2>
              <span className="label-icon">
                <Ruler size={16} /> Dimensions
              </span>
            </h2>
            <div className="field-row">
              <div className="field-group">
                <label htmlFor="width">Width (in)</label>
                <input
                  id="width"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.width}
                  onChange={(event) => handleFieldChange('width', event.target.value)}
                  className={validationErrors.width ? 'has-error' : ''}
                  placeholder="0"
                />
                {validationErrors.width && <p className="field-error">{validationErrors.width}</p>}
              </div>
              <div className="field-group">
                <label htmlFor="height">Height (in)</label>
                <input
                  id="height"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.height}
                  onChange={(event) => handleFieldChange('height', event.target.value)}
                  className={validationErrors.height ? 'has-error' : ''}
                  placeholder="0"
                />
                {validationErrors.height && <p className="field-error">{validationErrors.height}</p>}
              </div>
              <div className="field-group">
                <label htmlFor="depth">Depth (in)</label>
                <input
                  id="depth"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.depth}
                  onChange={(event) => handleFieldChange('depth', event.target.value)}
                  className={validationErrors.depth ? 'has-error' : ''}
                  placeholder="0"
                />
                {validationErrors.depth && <p className="field-error">{validationErrors.depth}</p>}
              </div>
            </div>

            <div className="field-group">
              <label htmlFor="completion_year">Completion Year</label>
              <input
                id="completion_year"
                type="text"
                inputMode="numeric"
                pattern="\\d{4}"
                maxLength={4}
                value={form.completion_year}
                onChange={(event) => handleFieldChange('completion_year', event.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                className={validationErrors.completion_year ? 'has-error' : ''}
                placeholder="e.g. 2024"
              />
              {validationErrors.completion_year && (
                <p className="field-error">{validationErrors.completion_year}</p>
              )}
            </div>
          </section>

          <section className="edit-card">
            <h2>
              <span className="label-icon">
                <Palette size={16} /> Medium & Description
              </span>
            </h2>

            <div className="field-group">
              <label htmlFor="description">Medium</label>
              <textarea
                id="description"
                rows={4}
                value={form.description}
                onChange={(event) => handleFieldChange('description', event.target.value)}
                placeholder="e.g. Oil on canvas"
              />
            </div>
          </section>

          <section className="edit-card">
            <h2>
              <span className="label-icon">
                <MapPin size={16} /> Gallery Location
              </span>
            </h2>
            <div className="field-group">
              <label htmlFor="location">Current Location</label>
              <select
                id="location"
                value={form.location}
                onChange={(event) => handleFieldChange('location', event.target.value)}
              >
                <option value="">No gallery assigned</option>
                {accessibleGalleries.map((gallery) => (
                  <option key={gallery.gallery_id} value={gallery.gallery_id}>
                    {gallery.name}
                  </option>
                ))}
              </select>
              <p className="field-hint">
                This list shows galleries you can manage. Assigning a gallery updates the on-site location for the artwork.
              </p>
            </div>
          </section>

          <section className="edit-card info-card">
            <h2>
              <span className="label-icon">
                <Info size={16} /> Artwork Metadata
              </span>
            </h2>
            <div className="meta-grid">
              <div>
                <span className="meta-label">Created</span>
                <span className="meta-value">
                  {artwork.created_at ? new Date(artwork.created_at).toLocaleString() : 'Unknown'}
                </span>
              </div>
              <div>
                <span className="meta-label">Updated</span>
                <span className="meta-value">
                  {artwork.updated_at ? new Date(artwork.updated_at).toLocaleString() : 'Never'}
                </span>
              </div>
              <div>
                <span className="meta-label">VC ID</span>
                <span className="meta-value">{artwork.vc_id}</span>
              </div>
              <div>
                <span className="meta-label">Artist UUID</span>
                <span className="meta-value">{artwork.users?.uuid || '—'}</span>
              </div>
            </div>
          </section>
        </main>
      )}

      {/* Images Tab */}
      {activeTab === 'images' && (
        <main className="curator-edit-content">
          {/* Cover Image Section */}
          <section className="edit-card">
            <h2>Cover Image</h2>
            <p className="section-description">
              This is the main image of your artwork. It should be a straight-on, well-lit photo that accurately represents your piece.
            </p>
            
            {/* Hidden file input for cover image */}
            <input
              type="file"
              ref={coverImageInputRef}
              accept="image/*"
              onChange={processCoverImageUpload}
              style={{ display: 'none' }}
            />
            
            <div className={`cover-image-display ${uploadingCoverImage ? 'uploading' : ''}`}>
              {uploadingCoverImage ? (
                <div className="cover-uploading">
                  <LoadingSpinner />
                  <p>Uploading cover image...</p>
                </div>
              ) : (
                <img
                  src={coverImage || '/images/placeholder-artwork.jpg'}
                  alt={form.title || 'Cover image'}
                  onError={(event) => {
                    event.currentTarget.src = '/images/placeholder-artwork.jpg';
                  }}
                />
              )}
            </div>
            
            <button
              type="button"
              className="btn-replace-cover"
              onClick={() => coverImageInputRef.current?.click()}
              disabled={uploadingCoverImage}
            >
              <Upload size={16} />
              {coverImage ? 'Replace Cover Image' : 'Upload Cover Image'}
            </button>
          </section>

          {/* Additional Images Section */}
          <section className="edit-card">
            <h2>Additional Images</h2>
            <p className="section-description">
              High-quality images help collectors appreciate your artwork's details, scale, and craftsmanship. Include photos from different angles and contexts to give buyers confidence in their purchase.
            </p>
            
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              onChange={processImageUpload}
              style={{ display: 'none' }}
            />

            <div className="additional-images-grid">
              {[
                { type: 'On-Wall', description: 'Show artwork displayed on wall' },
                { type: 'Framed', description: 'Display with frame presentation' },
                { type: 'Close-Up', description: 'Highlight intricate details' },
                { type: 'Detail', description: 'Show textures and brushstrokes' },
                { type: 'Signed', description: 'Display your signature' },
                { type: 'Unframed', description: 'Show raw edges' },
                { type: 'In-Progress', description: 'Work in progress photo' },
                { type: 'Other', description: 'Additional photos' }
              ].map((imageType) => {
                const existingImage = mediaElements.find(
                  (m) => m.title?.toLowerCase() === imageType.type.toLowerCase() && m.type === 'Image'
                );
                const isUploading = uploadingImageType === imageType.type;
                
                return (
                  <div
                    key={imageType.type}
                    className={`additional-image-card ${existingImage ? 'has-image' : ''} ${isUploading ? 'uploading' : ''}`}
                  >
                    {isUploading ? (
                      <div className="card-uploading">
                        <LoadingSpinner />
                        <p>Uploading...</p>
                      </div>
                    ) : existingImage ? (
                      <>
                        <div className="card-image">
                          <img
                            src={existingImage.source || existingImage.cover_image}
                            alt={imageType.type}
                            onError={(event) => {
                              event.currentTarget.src = '/images/placeholder-artwork.jpg';
                            }}
                          />
                          <span className="card-type-label">{imageType.type}</span>
                        </div>
                        <div className="card-actions">
                          <button
                            type="button"
                            className="btn-card-action replace"
                            onClick={() => handleImageUpload(imageType.type)}
                            title="Replace image"
                          >
                            <Upload size={14} />
                            Replace
                          </button>
                          <button
                            type="button"
                            className="btn-card-action delete"
                            onClick={() => handleDeleteImage(existingImage.element_id, imageType.type)}
                            title="Delete image"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div 
                        className="card-placeholder clickable"
                        onClick={() => handleImageUpload(imageType.type)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleImageUpload(imageType.type)}
                      >
                        <Upload size={24} className="upload-icon" />
                        <h4>{imageType.type}</h4>
                        <p>{imageType.description}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </main>
      )}

      {/* Tags Tab */}
      {activeTab === 'tags' && (
        <main className="curator-edit-content">
          {/* Artwork reference image */}
          {coverImage && (
            <div className="tags-artwork-ref">
              <img
                src={coverImage.includes('cloudinary.com') && coverImage.includes('/upload/')
                  ? coverImage.replace('/upload/', '/upload/t_fitmax-300/')
                  : coverImage}
                alt={artwork?.title || 'Artwork'}
                className="tags-artwork-img"
              />
              {artwork?.title && <p className="tags-artwork-title">{artwork.title}</p>}
              {(artwork?.width || artwork?.height) && (
                <p className="tags-artwork-dims">
                  {artwork.width && artwork.height
                    ? `${artwork.width}" × ${artwork.height}"${artwork.depth ? ` × ${artwork.depth}"` : ''}`
                    : artwork.width ? `${artwork.width}" wide` : `${artwork.height}" tall`}
                </p>
              )}
            </div>
          )}

          <section className="edit-card">
            <h2>
              <span className="label-icon">
                <Tag size={16} /> Artwork Tags
              </span>
            </h2>
            <p className="tags-intro">Thoughtful tagging helps your artwork reach collectors and increases discovery.</p>
            
            {loadingTags ? (
              <div className="loading-state">
                <LoadingSpinner />
                <p>Loading tags...</p>
              </div>
            ) : (
              <>
                {/* Style Tags */}
                {(() => {
                  const styleTags = allTags.filter(t => t.type === 'style');
                  const assignedCount = artworkTags.filter(t => t.type === 'style').length;
                  if (styleTags.length === 0) return null;
                  return (
                    <div className="tag-type-section">
                      <div className="tag-type-header">
                        <h3>Style</h3>
                        <span className="tag-type-hint">choose up to 3</span>
                      </div>
                      <p className="tag-type-desc">What artistic style best describes your work?</p>
                      <div className="tag-toggle-list">
                        {styleTags.map(tag => {
                          const assigned = artworkTags.find(at => at.attributeId === tag.id);
                          const canSelect = assigned || assignedCount < 3;
                          return (
                            <button
                              key={tag.id}
                              type="button"
                              className={`tag-toggle ${assigned ? 'active' : ''} ${!canSelect ? 'disabled' : ''}`}
                              disabled={addingTag || !canSelect}
                              onClick={() => assigned ? handleRemoveTag(assigned.mappingId) : handleAddTag(tag)}
                            >
                              {tag.title}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Subject Tags */}
                {(() => {
                  const subjectTags = allTags.filter(t => t.type === 'subject');
                  const assignedCount = artworkTags.filter(t => t.type === 'subject').length;
                  if (subjectTags.length === 0) return null;
                  return (
                    <div className="tag-type-section">
                      <div className="tag-type-header">
                        <h3>Subject</h3>
                        <span className="tag-type-hint">choose up to 3</span>
                      </div>
                      <p className="tag-type-desc">What themes or subjects does your artwork explore?</p>
                      <div className="tag-toggle-list">
                        {subjectTags.map(tag => {
                          const assigned = artworkTags.find(at => at.attributeId === tag.id);
                          const canSelect = assigned || assignedCount < 3;
                          return (
                            <button
                              key={tag.id}
                              type="button"
                              className={`tag-toggle ${assigned ? 'active' : ''} ${!canSelect ? 'disabled' : ''}`}
                              disabled={addingTag || !canSelect}
                              onClick={() => assigned ? handleRemoveTag(assigned.mappingId) : handleAddTag(tag)}
                            >
                              {tag.title}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Feeling Tags */}
                {(() => {
                  const feelingTags = allTags.filter(t => t.type === 'feeling');
                  if (feelingTags.length === 0) return null;
                  return (
                    <div className="tag-type-section">
                      <div className="tag-type-header">
                        <h3>Feeling</h3>
                      </div>
                      <p className="tag-type-desc">What mood or emotion does your artwork evoke?</p>
                      <div className="tag-toggle-list">
                        {feelingTags.map(tag => {
                          const assigned = artworkTags.find(at => at.attributeId === tag.id);
                          return (
                            <button
                              key={tag.id}
                              type="button"
                              className={`tag-toggle ${assigned ? 'active' : ''}`}
                              disabled={addingTag}
                              onClick={() => assigned ? handleRemoveTag(assigned.mappingId) : handleAddTag(tag)}
                            >
                              {tag.title}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Color Tags */}
                {(() => {
                  const colorTags = allTags.filter(t => t.type === 'color');
                  if (colorTags.length === 0) return null;
                  return (
                    <div className="tag-type-section">
                      <div className="tag-type-header">
                        <h3>Color Palette</h3>
                      </div>
                      <p className="tag-type-desc">Help collectors filter by color palette.</p>
                      <div className="tag-toggle-list">
                        {colorTags.map(tag => {
                          const assigned = artworkTags.find(at => at.attributeId === tag.id);
                          return (
                            <button
                              key={tag.id}
                              type="button"
                              className={`tag-toggle ${assigned ? 'active' : ''}`}
                              disabled={addingTag}
                              onClick={() => assigned ? handleRemoveTag(assigned.mappingId) : handleAddTag(tag)}
                            >
                              {tag.title}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Color Tones */}
                {(() => {
                  const toneTags = allTags.filter(t => t.type === 'tone');
                  if (toneTags.length === 0) return null;
                  return (
                    <div className="tag-type-section">
                      <div className="tag-type-header">
                        <h3>Color Tones</h3>
                      </div>
                      <div className="tone-chips">
                        {toneTags.map(tone => {
                          const assigned = artworkTags.find(at => at.attributeId === tone.id);
                          return (
                            <button
                              key={tone.id}
                              type="button"
                              className={`tone-chip ${assigned ? 'active' : ''}`}
                              disabled={addingTag}
                              onClick={() => assigned ? handleRemoveTag(assigned.mappingId) : handleAddTag(tone)}
                            >
                              {tone.title}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </section>
        </main>
      )}

      {/* Story Tab */}
      {activeTab === 'story' && (
        <main className="curator-edit-content">
          <section className="edit-card">
            <h2>
              <span className="label-icon">
                <BookOpen size={16} /> Share Your Art&apos;s Story
              </span>
            </h2>
            <p className="story-intro">
              VibrantCast encourages artists to add context and background to their work. 
              This helps collectors connect with your art on a deeper level.
            </p>

            <div className="story-section">
              <h3>Inspiration</h3>
              <p className="story-description">
                What inspires your work? Share the ideas, emotions, or experiences that influence your art.
              </p>
              <textarea
                className="story-textarea"
                value={storyForm.inspiration}
                onChange={(e) => setStoryForm(prev => ({ ...prev, inspiration: e.target.value }))}
                placeholder="Write your inspiration here..."
                rows={6}
              />
            </div>

            <div className="story-section">
              <h3>Process</h3>
              <p className="story-description">
                Describe how you create your art. What steps do you take, and what materials or tools do you use?
              </p>
              <textarea
                className="story-textarea"
                value={storyForm.process}
                onChange={(e) => setStoryForm(prev => ({ ...prev, process: e.target.value }))}
                placeholder="Write your process here..."
                rows={6}
              />
            </div>

            {storySuccess && (
              <div className="feedback success">
                <CheckCircle size={16} /> {storySuccess}
              </div>
            )}

            <button
              type="button"
              className="save-btn story-save-btn"
              onClick={handleSaveStory}
              disabled={savingStory}
            >
              {savingStory ? (
                <><RefreshCw className="spin" size={16} /> Saving...</>
              ) : (
                <><Save size={16} /> Save Story</>
              )}
            </button>
          </section>
        </main>
      )}

      {/* Prints Tab */}
      {activeTab === 'prints' && (
        <main className="curator-edit-content">
          {/* High-Res Print Source Section */}
          <section className="edit-card highres-section">
            <h2>
              <span className="label-icon">
                <FileImage size={16} /> High-Resolution Print Source
              </span>
            </h2>
            
            <p className="section-description">
              Upload a high-resolution image (up to 100MB) for print production. 
              For quality prints at 240 DPI, the image should be at least 4,800px on the longest side for 20" prints.
            </p>
            
            {/* Current Status */}
            {loadingPrintStatus ? (
              <div className="loading-inline">
                <RefreshCw className="spin" size={16} />
                <span>Loading print status...</span>
              </div>
            ) : printStatus ? (
              <div className="print-status-display">
                <div className="status-header">
                  <span 
                    className="quality-badge"
                    style={{ backgroundColor: getQualityColor(printStatus.quality_level) }}
                  >
                    {printStatus.quality_level === 'excellent' && <CheckCircle size={14} />}
                    {printStatus.quality_level === 'good' && <Check size={14} />}
                    {printStatus.quality_level === 'acceptable' && <Clock size={14} />}
                    {printStatus.quality_level === 'low' && <X size={14} />}
                    {printStatus.quality_level?.charAt(0).toUpperCase() + printStatus.quality_level?.slice(1) || 'Unknown'}
                  </span>
                  {printStatus.print_enabled && (
                    <span className="enabled-badge">
                      <Printer size={12} /> Print Enabled
                    </span>
                  )}
                </div>
                
                <div className="status-details">
                  <div className="detail-row">
                    <span className="label">Image Size:</span>
                    <span className="value">
                      {printStatus.image_width?.toLocaleString()} × {printStatus.image_height?.toLocaleString()} px
                    </span>
                  </div>
                  {printStatus.image_bytes && (
                    <div className="detail-row">
                      <span className="label">File Size:</span>
                      <span className="value">{formatFileSize(printStatus.image_bytes)}</span>
                    </div>
                  )}
                  {printStatus.quality_checked_at && (
                    <div className="detail-row">
                      <span className="label">Last Scanned:</span>
                      <span className="value">
                        {new Date(printStatus.quality_checked_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Available Print Sizes */}
                <div className="available-sizes-section">
                  <h4>Available Print Sizes (240 DPI)</h4>
                  <div className="sizes-grid">
                    {Object.entries(PRINT_DIMENSIONS_240DPI).map(([size, dims]) => {
                      const isAvailable = printStatus.available_sizes?.includes(size);
                      return (
                        <span 
                          key={size} 
                          className={`size-tag ${isAvailable ? 'available' : 'unavailable'}`}
                          title={
                            isAvailable 
                              ? `${dims.label} available (requires ${dims.width}×${dims.height}px)`
                              : `${dims.label} requires ${dims.width}×${dims.height}px`
                          }
                        >
                          {isAvailable ? <Check size={10} /> : <X size={10} />}
                          {dims.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
                
                {/* High-res file link */}
                {printStatus.highres_image_url && (
                  <div className="highres-link">
                    <a 
                      href={printStatus.highres_image_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn-link"
                    >
                      <ExternalLink size={14} />
                      View High-Res Source File
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div className="no-print-status">
                <AlertCircle size={24} />
                <p>No print quality data available. Scan the existing image or upload a high-resolution file.</p>
              </div>
            )}
            
            {/* Upload and Scan Actions */}
            <div className="highres-actions">
              {/* Hidden file input for high-res upload */}
              <input
                ref={highResInputRef}
                type="file"
                accept="image/jpeg,image/png,image/tiff,image/heic,image/heif,image/webp,.tif,.tiff"
                style={{ display: 'none' }}
                onChange={(e) => handleHighResUpload(e.target.files?.[0])}
                disabled={uploadingHighRes}
              />
              
              <button
                type="button"
                className="btn-primary"
                onClick={() => highResInputRef.current?.click()}
                disabled={uploadingHighRes}
              >
                {uploadingHighRes ? (
                  <><RefreshCw className="spin" size={16} /> Uploading...</>
                ) : (
                  <><Upload size={16} /> {printStatus?.highres_image_url ? 'Replace High-Res' : 'Upload High-Res Image'}</>
                )}
              </button>
              
              <button
                type="button"
                className="btn-secondary"
                onClick={scanExistingImageQuality}
                disabled={scanningQuality || (!coverImage && !printStatus?.highres_image_url)}
                title={printStatus?.highres_image_url 
                  ? 'Re-analyze the high-res Supabase image' 
                  : coverImage 
                    ? 'Analyze the current Cloudinary cover image' 
                    : 'No image to scan'}
              >
                {scanningQuality ? (
                  <><RefreshCw className="spin" size={16} /> Scanning...</>
                ) : (
                  <><Zap size={16} /> {printStatus?.highres_image_url ? 'Re-Scan High-Res' : 'Scan Cover Image'}</>
                )}
              </button>
              
              <button
                type="button"
                className="btn-secondary"
                onClick={analyzeImageColors}
                disabled={analyzingColors || (!coverImage && !printStatus?.highres_image_url)}
                title="Analyze dominant colors using Cloudinary"
              >
                {analyzingColors ? (
                  <><RefreshCw className="spin" size={16} /> Analyzing...</>
                ) : (
                  <><Palette size={16} /> Analyze Colors</>
                )}
              </button>
            </div>
            
            <div className="upload-notes">
              <p><strong>Workflow:</strong> First scan the cover image. If quality is insufficient for printing, upload a high-res version (from RAW/professional camera).</p>
              <p><strong>Supported formats:</strong> JPEG, PNG, TIFF, HEIC, HEIF, WebP (up to 100MB)</p>
              <p><strong>For 20" prints:</strong> Minimum 4,800px on longest side at 240 DPI</p>
              <p><strong>Pro Tip:</strong> Professional RAW images (Canon 5D, etc.) processed in Lightroom provide more accurate colors than iPhone shots.</p>
            </div>
          </section>

          {/* Existing Prints/Products Section */}
          <section className="edit-card">
            <h2>
              <span className="label-icon">
                <Printer size={16} /> Print Products
              </span>
            </h2>
            
            {prints.length === 0 ? (
              <div className="empty-state">
                <Printer size={48} />
                <p>No print products have been created for this artwork yet.</p>
                <p className="hint">Print products can be configured through the Print Campaign manager.</p>
              </div>
            ) : (
              <div className="prints-list">
                {prints.map((print) => (
                  <div key={print.product_id} className="print-item">
                    <div className="print-image">
                      {print.cover_image ? (
                        <img
                          src={print.cover_image}
                          alt={print.title || 'Print'}
                          onError={(event) => {
                            event.currentTarget.src = '/images/placeholder-artwork.jpg';
                          }}
                        />
                      ) : (
                        <div className="no-image">
                          <Printer size={24} />
                        </div>
                      )}
                    </div>
                    <div className="print-details">
                      <h4>{print.title || 'Untitled Print'}</h4>
                      <p className="print-description">{print.description || 'No description'}</p>
                      <div className="print-meta">
                        <span className="print-price">
                          <DollarSign size={14} />
                          {parseFloat(print.price || 0).toFixed(2)}
                        </span>
                        <span className={`print-status ${print.status?.toLowerCase().replace(/\s/g, '-')}`}>
                          {print.status || 'Unknown'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </main>
      )}
    </div>
  );
};

export default CuratorArtworkEdit;
